import React, { useState } from 'react';
import { Box, Typography, TextField, Button, Stack, IconButton } from '@mui/material';
import { AttachEmail } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { allImageFileTypesWithVideo } from '../../../../components/file/utils';

interface MessageFormProps {
  onMessageSubmit: (message: string, file?: File) => Promise<void>;
}

const MAX_CHARACTERS = 500;

export const MessageForm: React.FC<MessageFormProps> = ({ onMessageSubmit }) => {
  const [message, setMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const { enqueueSnackbar } = useSnackbar();

  const fileTypes = allImageFileTypesWithVideo.filter(x => x !== '.tiff');
  const remainingCharacters = MAX_CHARACTERS - message.length;

  const handleFileChange = (file: File) => {
    if (file) {
      const uploadedFileType = file.type.split?.('/')?.[1];
      if (
        //The mime type for .mov files is 'video/quicktime' they don't use the extension like other file types
        uploadedFileType !== 'quicktime' &&
        !fileTypes
          .map(extension => extension.replace(/\./g, ''))
          .some(type => type === uploadedFileType)
      ) {
        enqueueSnackbar(
          `Invalid file type, ${uploadedFileType}. Please upload one of these file types: ${fileTypes.join(
            ', '
          )}.`,
          {
            variant: 'error'
          }
        );
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleCapture = async (target: EventTarget & HTMLInputElement) => {
    if (target.files && target.files.length > 0) {
      handleFileChange(target.files[0]);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
  };

  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    // Only update if within character limit
    if (newValue.length <= MAX_CHARACTERS) {
      setMessage(newValue);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onMessageSubmit(message.trim(), selectedFile || undefined);
      setMessage('');
      setSelectedFile(null);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Stack spacing={3} sx={{ height: '100%' }}>
      <Box>
        <Typography
          variant="body1"
          sx={{
            fontWeight: 900,
            mb: 2
          }}
        >
          Have a question for your build team?
        </Typography>
        <Typography variant="body1">
          Use the comment box below to leave it for us! All questions will be reviewed by end of day
          Wednesday, and someone from the team will follow up with a response shortly after. We're
          here to help and keep you informed every step of the way!
        </Typography>
      </Box>

      <Box component="form" onSubmit={handleSubmit}>
        <Box sx={{ position: 'relative' }}>
          <TextField
            multiline
            rows={10}
            placeholder="Type your message here..."
            value={message}
            onChange={handleMessageChange}
            variant="outlined"
            fullWidth
            inputProps={{
              maxLength: MAX_CHARACTERS
            }}
            sx={{
              flex: 1,
              backgroundColor: 'white',
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  border: 'none'
                },
                '&:hover fieldset': {
                  border: 'none'
                },
                '&.Mui-focused fieldset': {
                  border: 'none'
                }
              }
            }}
          />

          {/* Character Counter */}
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 12,
              color: remainingCharacters <= 50 ? 'warning.main' : 'text.secondary',
              backgroundColor: 'white',
              px: 1,
              fontSize: '0.75rem',
              fontWeight: remainingCharacters <= 50 ? 600 : 400
            }}
          >
            {remainingCharacters} characters left
          </Typography>
        </Box>

        {/* File Upload */}
        <Box display="flex" alignItems="center">
          <input
            accept={fileTypes.join(',')}
            disabled={isSubmitting}
            style={{ display: 'none' }}
            id="message-attachment-input"
            type="file"
            onChange={e => {
              handleCapture(e.target);
              // reset the input value so you can re-upload the same file if you want to
              e.target.value = '';
            }}
          />
          <label htmlFor="message-attachment-input">
            <IconButton
              sx={{ color: theme => theme.palette.primary.dark }}
              disabled={isSubmitting}
              aria-label="attach file"
              component="span"
              size="medium"
            >
              <AttachEmail fontSize="medium" />
            </IconButton>
          </label>
          {selectedFile && (
            <>
              <Typography
                variant="body2"
                color="textSecondary"
                sx={{
                  maxWidth: '200px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {selectedFile.name}
              </Typography>
              <IconButton
                size="small"
                onClick={handleRemoveFile}
                disabled={isSubmitting}
                sx={{ padding: '4px' }}
              >
                ×
              </IconButton>
            </>
          )}
        </Box>

        {/* Submit Button */}
        <Box display="flex" justifyContent="center">
          <Button
            type="submit"
            variant="contained"
            disabled={!message.trim() || isSubmitting}
            sx={{
              minWidth: '250px',
              '&:disabled': {
                backgroundColor: 'grey.300',
                color: 'grey.500'
              }
            }}
          >
            {isSubmitting ? 'Sending...' : 'Submit'}
          </Button>
        </Box>
      </Box>
    </Stack>
  );
};
